import { PAL, PAL3D } from "../paletaDesen";
import { FemRadier, FemResult, FemZidarieTurnata } from "../tipuri";
import { numar } from "../../i18n";

/** Minimul și maximul unei liste, într-o trecere. `Math.min(...lista)` pune
 *  fiecare valoare ca argument pe stivă și cade la câteva zeci de mii de
 *  noduri — exact mărimea unui model cu multe etaje. */
export function intinderea(v: readonly number[]): { min: number; max: number } {
  let min = Infinity, max = -Infinity;
  for (const x of v) { if (x < min) min = x; if (x > max) max = x; }
  return { min, max };
}

// Pasul 7 — Modelarea structurală (FEM): reprezentarea 3D a modelului de
// analiză OpenSees (stâlpi, grinzi T, punctele de împărțire FEM, diafragmele
// cu centrul de masă) + verificarea modală. Click pe o grindă / un stâlp
// deschide panoul cu proprietățile lui (secțiune, b_eff, rigidități,
// încărcări) — plus verificarea modală a modelului.

export type Selectie =
  | { tip: "grinda"; ref: number }
  | { tip: "stalp"; ref: number }
  | { tip: "perete_beton"; ref: number }
  // perete structural de zidărie confinată: `ref` în `model.pereti_zidarie`
  | { tip: "zidarie_confinata"; ref: number }
  | { tip: "perete_zidarie"; ref: number }
  | { tip: "fereastra"; ref: number }
  | { tip: "usa"; ref: number }
  | { tip: "parapet"; ref: number }
  | { tip: "panou"; ref: number }
  // grinda de fundare: `ref` e indicele tronsonului din `model.grinzi_fundare`
  | { tip: "fundatie"; ref: number }
  // placa pe sol: `ref` e indicele componentei din `model.placa_pe_sol.contur`
  | { tip: "placa_pe_sol"; ref: number }
  // radierul: `ref` e indicele în `radiereDesenate(model)` (al corpului
  // singur, sau al unui corp al complexului)
  | { tip: "radier"; ref: number }
  | null;

// pereții nestructurali, aplatizați în ORDINEA în care îi desenează Scene3D —
// `ref`-ul unei selecții e indexul în lista asta, deci construcția trebuie să
// rămână identică în ambele locuri
export function zidarieFlat(model: FemResult["model"]) {
  return model.etaje.flatMap((et) =>
    (et.pereti ?? []).map((w) => ({ ...w, nivel: et.nivel }))
  );
}

// Sub atâta din perete, piesa de beton n-a LUAT LOCUL peretelui — doar i-a
// ciupit un capăt, iar „turnat peste E64" ar fi o exagerare. Pe etajul 4 al
// lui «Olteniței P + 4» pragul desparte lamela care a luat 99 % din E64 de
// stâlpii de colț care au luat 6 %.
const PCT_A_LUAT_LOCUL = 50;

/** Peretele Pasului 2 al cărui loc l-a luat piesa de beton `id`, dacă e unul.
 *
 *  E răspunsul la «de ce nu-l mai văd pe E64?», pus în panoul piesei care
 *  i-a luat locul: peretele nu s-a pierdut, s-a turnat — pe «Olteniței P + 4»
 *  lamela ST117.6 de 30 × 690 cm stă exact pe el. Fără rândul ăsta, singura
 *  urmă a peretelui era absența lui.
 *
 *  Se caută după partea PIESEI, nu după cât s-a turnat din perete: un stâlp
 *  care a ciupit 6 % dintr-un perete turnat integral n-a înlocuit nimic. */
export function pereteTurnatIn(model: FemResult["model"], id?: string | null) {
  if (!id) return null;
  let gasit: { zid: FemZidarieTurnata; nivel: string; pct: number } | null =
    null;
  for (const et of model.etaje)
    for (const zid of et.zidarie_turnata ?? []) {
      const parte = zid.elemente.find((e) => e.id === id);
      if (!parte || parte.pct < PCT_A_LUAT_LOCUL) continue;
      if (!gasit || parte.pct > gasit.pct)
        gasit = { zid, nivel: et.nivel, pct: parte.pct };
    }
  return gasit;
}

// Vocabularul de materiale al arhitecturii (`tronsoane.schema.json`), legat
// de textele lui. Tabel, nu cheie construită din șir: `Cheie` e o uniune, iar
// un material necunoscut trebuie să se vadă ca lipsă, nu să treacă drept text.
export const MATERIAL_CHEIE = {
  beton: "prop.material.beton",
  zidarie: "prop.material.zidarie",
  polistiren: "prop.material.polistiren",
  gips_carton: "prop.material.gips_carton",
  sticla: "prop.material.sticla",
} as const;

/** Cât s-a turnat din peretele `id` al nivelului `nivel`, și cu ce piese —
 *  pentru peretele care a rămas doar în parte (celălalt capăt e beton). */
export function turnatDinPerete(
  model: FemResult["model"], id: string, nivel: string
) {
  const et = model.etaje.find((e) => e.nivel === nivel);
  return (et?.zidarie_turnata ?? []).find((z) => z.id_plan === id) ?? null;
}

// golurile (ferestre + uși) și parapetele, aplatizate în aceeași ordine în
// care le desenează Scene3D — `ref`-ul selecției e indexul în lista asta.
// Ferestrele și ușile stau în DOUĂ liste, fiindcă sunt două layere separate
// în panoul de vizibilitate; ordinea în fiecare e ordinea desenării.
export function goluriFlat(model: FemResult["model"], tip: "fereastra" | "usa") {
  return model.etaje.flatMap((et) =>
    (et.goluri ?? [])
      .filter((g) => (g.tip === "usa" ? "usa" : "fereastra") === tip)
      .map((g) => ({ ...g, nivel: et.nivel, z_m: et.z_m, h_etaj_m: et.inaltime_m }))
  );
}

/** Fundația DE DESENAT și dacă ea e în modelul de calcul.
 *
 *  Când baza e încastrată, betonul de sub clădire există fizic — doar că nu
 *  e element de calcul. Geometria lui vine atunci din `grinzi_fundare_desen`
 *  (v. `fem_fundatie.geometrie_de_desen`), iar layerul e stins implicit:
 *  «e ok să o dezactivăm by default când e cazul ăsta, dar vreau să o pot
 * readăuga să o văd». */
export function fundareaDesenata(model: FemResult["model"]) {
  const inModel = (model.grinzi_fundare ?? []).length > 0;
  return {
    inModel,
    grinzi: inModel
      ? model.grinzi_fundare ?? []
      : model.grinzi_fundare_desen ?? [],
    fnd: (inModel ? model.fundatie : model.fundatie_desen) ?? {},
  };
}

/** Radierele de desenat: ale complexului (`model.radiere`, fiecare corp la
 *  cota lui) sau al corpului singur, din conturul radierului lui
 *  (`fundatie.radier.contur`): de la pardoseala nivelului de jos (cota 0)
 *  până la −h_r, pe balastul plăcii pe sol. Gol pe tălpi. */
export function radiereDesenate(model: FemResult["model"]): FemRadier[] {
  if (model.radiere) return model.radiere;
  const rad = model.fundatie?.radier;
  if (!rad?.contur?.length || !(rad.h_m > 0)) return [];
  return [{
    h_m: rad.h_m, amprenta_m2: rad.amprenta_m2 ?? null,
    balast_m: model.placa_pe_sol?.balast_m ?? 0,
    z_sus_m: 0, z_jos_m: -rad.h_m, contur: rad.contur,
  }];
}

/** Placa pe sol se desenează câtă vreme nu e radierul pardoseala: pe un
 *  corp singur pe radier NU (devizul n-o toarnă nici el, `cantitati.calcul`);
 *  pe complex da — serverul a lăsat în ea numai plăcile corpurilor pe tălpi
 *  și placa parcării. */
export function placaPeSolDesenata(model: FemResult["model"]) {
  const psol = model.placa_pe_sol;
  if (!psol?.contur?.length) return null;
  if (!model.radiere && radiereDesenate(model).length) return null;
  return psol;
}

export function parapeteFlat(model: FemResult["model"]) {
  return model.etaje.flatMap((et) =>
    (et.margini ?? []).map((m) => ({ ...m, nivel: et.nivel }))
  );
}

/** Casele de scară și lifturile tuturor nivelurilor, cu nivelul lor. */
export function nucleeFlat(model: FemResult["model"]) {
  return model.etaje.flatMap((et) =>
    (et.nuclee ?? []).map((n) => ({ ...n, nivel: et.nivel }))
  );
}

// Paleta scenei vine din `paletaDesen.ts` — unica paletă a desenelor. Numele
// locale (`CULORI` pentru three.js, `CULORI_CSS` pentru legende și texte)
// rămân, ca restul fișierului să nu se schimbe.
export const CULORI = PAL3D;

/** Pasul grilei de sub clădire, în metri. Reper de scară, nu caroiaj:
 *  liniile mai dese decât atât nu se mai pot desena stabil la unghiuri
 *  razante. */
export const PAS_GRILA_M = 5;
export const CULORI_CSS = PAL;

// ---------------------------------------------------------------------------
// Subsolul rigid: pereții de beton dintre stâlpii subsolului + verdictul
// de rigiditate (P+2 sau peste, cu subsol — cutia rigidă a infrastructurii)
// ---------------------------------------------------------------------------

/** O cifră, în convenția limbii — virgulă în română, punct în engleză.
 *  Se chema `nrRo` și lipea virgula cu mâna; `numar` o ia din limba activă. */
export function nr(v: number | undefined | null, zecimale = 2): string {
  if (v === undefined || v === null) return "—";
  return numar(v, { minimumFractionDigits: zecimale,
                    maximumFractionDigits: zecimale });
}

// ---------------------------------------------------------------------------
// three.js
// ---------------------------------------------------------------------------

// exportată: vizualizatorul de soluții al Pasului 19 randează cu ea modelul
// oricărei clone-soluție
export type SelectieFem = Selectie;
